/**
 * Versioned wire protocol shared by the browser and the signaling server.
 *
 * JSON messages are deliberately small and never contain file bytes. File bytes
 * use the binary relay/data-channel frame below; the relay sees only the outer
 * routing header and encrypted payload.
 */

export const PROTOCOL_VERSION = 1 as const;
export const RELAY_MAGIC = 0x4452 as const; // "DR"

export type DeviceKind = 'phone' | 'tablet' | 'laptop' | 'desktop' | 'unknown';

export interface WireProfile {
  deviceId: string;
  name: string;
  avatar: string;
  color: string;
  kind: DeviceKind;
  platform: string;
  ecdhPublicKey?: JsonWebKey;
}

export interface WirePeer extends WireProfile {
  online: boolean;
  sources: Array<'nearby' | 'paired' | 'room' | 'link'>;
  autoAccept: boolean;
}

export interface HelloMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'hello';
  device: WireProfile;
  pairKeyHashes: string[];
  room?: string;
}

export interface WelcomeMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'welcome';
  peers: WirePeer[];
  room?: string;
  iceServers?: RTCIceServer[];
}

export interface PeerListMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'peers';
  peers: WirePeer[];
}

export interface PeerUpdateMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'peer.update';
  peer: WirePeer;
}

export interface PeerLeaveMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'peer.leave';
  peerId: string;
}

export interface PingMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'ping';
  at: number;
}

export interface PongMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pong';
  at: number;
}

export interface SignalMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'signal';
  to: string;
  kind: 'offer' | 'answer' | 'candidate' | 'renegotiate';
  data: unknown;
}

export interface TransferOfferMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'transfer.offer';
  to: string;
  transferId: string;
  files: WireFileMeta[];
}

export interface TransferDecisionMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'transfer.accept' | 'transfer.decline' | 'transfer.cancel';
  to: string;
  transferId: string;
}

export interface TransferAckMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'transfer.ack';
  to: string;
  transferId: string;
  fileIndex: number;
  offset: number;
}

export interface TransferCreditMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'transfer.credit';
  to: string;
  transferId: string;
  bytes: number;
}

export interface TransferResumeMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'transfer.resume';
  to: string;
  transferId: string;
  offsets: number[];
}

export interface TransferCompleteMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'transfer.complete';
  to: string;
  transferId: string;
}

export interface TextMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'text';
  to: string;
  id: string;
  text: string;
  at: number;
}

export interface PairCreateMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pair.create';
  code: string;
  pairHash: string;
}

export interface PairCreatedMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pair.created';
  code: string;
  expiresAt: number;
}

export interface PairJoinMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pair.join';
  code: string;
}

export interface PairRequestMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pair.request';
  requester: string;
  code: string;
  pairHash: string;
}

export interface PairKeyMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pair.key';
  to: string;
  code: string;
  pairKey: string;
}

export interface PairResultMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'pair.result';
  peer: WirePeer;
  pairKey: string;
}

export interface RoomCreateMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'room.create';
}

export interface RoomJoinMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'room.join';
  code: string;
}

export interface RoomLeaveMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'room.leave';
}

export interface RoomResultMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'room.result';
  code: string;
  peers: WirePeer[];
}

export interface ServerErrorMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'error';
  code: string;
  message: string;
}

export interface ShareRegisterMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.register';
  shareId: string;
}

export interface ShareUnregisterMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.unregister';
  shareId: string;
}

export interface ShareJoinMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.join';
  shareId: string;
}

export interface ShareInfoRequestMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.info.request';
  to: string;
  shareId: string;
}

export interface ShareInfoResponseMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.info.response';
  to: string;
  shareId: string;
  files: WireFileMeta[];
  passwordRequired: boolean;
  error?: string;
}

export interface ShareDownloadRequestMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.download.request';
  to: string;
  shareId: string;
  password?: string;
}

export interface ShareDownloadResponseMessage {
  v: typeof PROTOCOL_VERSION;
  type: 'share.download.response';
  to: string;
  shareId: string;
  error?: string;
}

export type JsonMessage =
  | HelloMessage | WelcomeMessage | PeerListMessage | PeerUpdateMessage | PeerLeaveMessage
  | PingMessage | PongMessage | SignalMessage | TransferOfferMessage | TransferDecisionMessage
  | TransferAckMessage | TransferCreditMessage | TransferResumeMessage | TextMessage
  | TransferCompleteMessage
  | PairCreateMessage | PairJoinMessage | PairRequestMessage | PairKeyMessage | PairResultMessage
  | PairCreatedMessage
  | RoomCreateMessage | RoomJoinMessage | RoomLeaveMessage | RoomResultMessage | ServerErrorMessage
  | ShareRegisterMessage | ShareUnregisterMessage | ShareJoinMessage | ShareInfoRequestMessage
  | ShareInfoResponseMessage | ShareDownloadRequestMessage | ShareDownloadResponseMessage;

export interface WireFileMeta {
  id: string;
  name: string;
  size: number;
  mime: string;
  lastModified?: number;
  relativePath?: string;
  thumbnail?: string;
}

/** A browser-to-server relay frame. The payload is encrypted by the client. */
export interface RelayFrame {
  kind: 1;
  peerId: string;
  payload: Uint8Array;
}

/** A data-channel/relay inner frame. It is opaque to the server. */
export interface ChunkFrame {
  kind: 1;
  transferId: string;
  fileIndex: number;
  offset: number;
  payload: Uint8Array;
}

export function isJsonMessage(value: unknown): value is JsonMessage {
  return Boolean(value && typeof value === 'object' && (value as { v?: unknown }).v === PROTOCOL_VERSION &&
    typeof (value as { type?: unknown }).type === 'string');
}

function textBytes(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function readText(bytes: Uint8Array, start: number, length: number): string {
  return new TextDecoder().decode(bytes.subarray(start, start + length));
}

/**
 * Outer frame: magic:u16, version:u8, kind:u8, peerLength:u16, peer UTF-8,
 * then the opaque payload. The server rewrites peerId to the sender on egress.
 */
export function encodeRelayFrame(peerId: string, payload: Uint8Array): ArrayBuffer {
  const peer = textBytes(peerId);
  const out = new Uint8Array(2 + 1 + 1 + 2 + peer.length + payload.length);
  const view = new DataView(out.buffer);
  view.setUint16(0, RELAY_MAGIC);
  view.setUint8(2, PROTOCOL_VERSION);
  view.setUint8(3, 1);
  view.setUint16(4, peer.length);
  out.set(peer, 6);
  out.set(payload, 6 + peer.length);
  return out.buffer;
}

export function decodeRelayFrame(input: ArrayBuffer | Uint8Array): RelayFrame | null {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.byteLength < 6) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (
    view.getUint16(0) !== RELAY_MAGIC ||
    view.getUint8(2) !== PROTOCOL_VERSION ||
    view.getUint8(3) !== 1
  ) {
    return null;
  }
  const peerLength = view.getUint16(4);
  if (bytes.byteLength < 6 + peerLength) return null;
  return { kind: 1, peerId: readText(bytes, 6, peerLength), payload: bytes.slice(6 + peerLength) };
}

/** Inner chunk frame: magic:u16, version:u8, kind:u8, idLength:u16, id, file:u32, offset:u64, bytes. */
export function encodeChunkFrame(frame: Omit<ChunkFrame, 'kind'>): Uint8Array {
  const id = textBytes(frame.transferId);
  const out = new Uint8Array(2 + 1 + 1 + 2 + id.length + 4 + 8 + frame.payload.length);
  const view = new DataView(out.buffer);
  view.setUint16(0, RELAY_MAGIC);
  view.setUint8(2, PROTOCOL_VERSION);
  view.setUint8(3, 1);
  view.setUint16(4, id.length);
  out.set(id, 6);
  view.setUint32(6 + id.length, frame.fileIndex);
  view.setBigUint64(10 + id.length, BigInt(frame.offset));
  out.set(frame.payload, 18 + id.length);
  return out;
}

export function decodeChunkFrame(input: ArrayBuffer | Uint8Array): ChunkFrame | null {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.byteLength < 18) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (
    view.getUint16(0) !== RELAY_MAGIC ||
    view.getUint8(2) !== PROTOCOL_VERSION ||
    view.getUint8(3) !== 1
  ) {
    return null;
  }
  const idLength = view.getUint16(4);
  const header = 18 + idLength;
  if (bytes.byteLength < header) return null;
  return {
    kind: 1,
    transferId: readText(bytes, 6, idLength),
    fileIndex: view.getUint32(6 + idLength),
    offset: Number(view.getBigUint64(10 + idLength)),
    payload: bytes.slice(header),
  };
}
